// Authentication service for standalone webmail client
// Supports: direct login, token from URL (redirect from admin), and session management

const getEnv = (key: keyof ImportMetaEnv, fallback: string): string => {
  try {
    return (import.meta.env[key] as string | undefined) ?? fallback;
  } catch {
    return fallback;
  }
};

// Production defaults: tenant-service at demo.mail.melpapp.ai, admin portal
// also at demo.mail.melpapp.ai (they share the host; webmail itself runs at
// demo.web.melpapp.ai). Dev keeps localhost so the dev server works against
// the local stack.
const __PROD_BUILD = (() => { try { return import.meta.env.PROD; } catch { return false; } })();
const DEFAULT_AUTH = __PROD_BUILD ? 'https://demo.mail.melpapp.ai' : 'http://localhost:8081';
const DEFAULT_ADMIN_PORTAL = __PROD_BUILD ? 'https://demo.mail.melpapp.ai' : 'http://localhost:5173';

const AUTH_BASE = getEnv('VITE_AUTH_BASE_URL', '') || getEnv('VITE_API_BASE_URL', DEFAULT_AUTH);
const ADMIN_PORTAL_URL = getEnv('VITE_ADMIN_PORTAL_URL', DEFAULT_ADMIN_PORTAL);

const parseErrorMessage = (text: string, fallback: string): string => {
  if (!text) return fallback;
  try {
    const json = JSON.parse(text);
    return json.message || json.error || fallback;
  } catch {
    return text || fallback;
  }
};

// Token storage keys.
// Switched from sessionStorage to localStorage so tokens survive tab close,
// browser restart, and new-tab navigation to the webmail. The 7-day refresh
// token is now actually usable for what it's named for. Same XSS surface as
// before (any JS on origin can read it); the email-content iframe is already
// sandboxed so untrusted email HTML can't reach this storage.
const TOKEN_KEY = 'webmail_access_token';
const REFRESH_TOKEN_KEY = 'webmail_refresh_token';
const TOKEN_EXPIRY_KEY = 'webmail_token_expiry';

const tokenStore: Storage | null = (() => {
  try {
    if (typeof window === 'undefined') return null;
    // One-shot migration for users who have a session from before this
    // change — copy any sessionStorage values forward so they don't get
    // logged out by the deploy.
    const ss = window.sessionStorage;
    const ls = window.localStorage;
    [TOKEN_KEY, REFRESH_TOKEN_KEY, TOKEN_EXPIRY_KEY].forEach((k) => {
      const fromSession = ss.getItem(k);
      if (fromSession && !ls.getItem(k)) ls.setItem(k, fromSession);
      // Don't proactively delete from sessionStorage — leave it; it'll die
      // with the tab and we won't read from it again.
    });
    return ls;
  } catch {
    try { return typeof window !== 'undefined' ? window.sessionStorage : null; } catch { return null; }
  }
})();

export interface AuthTokens {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  refreshExpiresIn?: number;
}

export interface LoginCredentials {
  username: string;
  password: string;
  otp?: string;
}

export const authService = {
  /**
   * Get token from URL query parameter (used when redirected from admin portal)
   */
  getTokenFromUrl(): { token: string; refreshToken?: string; expiresIn?: number } | null {
    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');
    const refreshToken = params.get('refresh_token');
    const expiresInRaw = params.get('expires_in');
    const expiresIn = expiresInRaw ? parseInt(expiresInRaw, 10) : undefined;

    if (token) {
      // Clean URL after extracting token
      const url = new URL(window.location.href);
      url.searchParams.delete('token');
      url.searchParams.delete('refresh_token');
      url.searchParams.delete('expires_in');
      window.history.replaceState({}, '', url.toString());

      return {
        token,
        refreshToken: refreshToken || undefined,
        expiresIn: typeof expiresIn === 'number' && Number.isFinite(expiresIn) && expiresIn > 0 ? expiresIn : undefined
      };
    }
    return null;
  },

  /**
   * Get tokens from persistent storage (localStorage).
   */
  getStoredTokens(): { accessToken: string; refreshToken: string; expiry: number } | null {
    try {
      if (!tokenStore) return null;
      const accessToken = tokenStore.getItem(TOKEN_KEY);
      const refreshToken = tokenStore.getItem(REFRESH_TOKEN_KEY);
      const expiry = tokenStore.getItem(TOKEN_EXPIRY_KEY);

      if (accessToken && refreshToken) {
        return {
          accessToken,
          refreshToken,
          expiry: expiry ? parseInt(expiry, 10) : 0
        };
      }
    } catch (e) {
      console.warn('[AuthService] Failed to read from token store:', e);
    }
    return null;
  },

  storeTokens(tokens: AuthTokens): void {
    try {
      if (!tokenStore) return;
      tokenStore.setItem(TOKEN_KEY, tokens.accessToken);
      tokenStore.setItem(REFRESH_TOKEN_KEY, tokens.refreshToken);
      const expiry = Date.now() + (tokens.expiresIn * 1000);
      tokenStore.setItem(TOKEN_EXPIRY_KEY, String(expiry));
    } catch (e) {
      console.warn('[AuthService] Failed to write to token store:', e);
    }
  },

  clearTokens(): void {
    try {
      if (tokenStore) {
        tokenStore.removeItem(TOKEN_KEY);
        tokenStore.removeItem(REFRESH_TOKEN_KEY);
        tokenStore.removeItem(TOKEN_EXPIRY_KEY);
      }
      // Also wipe any leftover session-storage values from before the
      // localStorage migration so a stale RT can't haunt a closed tab.
      try {
        if (typeof window !== 'undefined') {
          window.sessionStorage.removeItem(TOKEN_KEY);
          window.sessionStorage.removeItem(REFRESH_TOKEN_KEY);
          window.sessionStorage.removeItem(TOKEN_EXPIRY_KEY);
        }
      } catch {}
    } catch (e) {
      console.warn('[AuthService] Failed to clear token store:', e);
    }
  },

  /**
   * Check if stored token is expired or about to expire
   */
  isTokenExpired(bufferSeconds: number = 60): boolean {
    const stored = this.getStoredTokens();
    if (!stored) return true;

    const now = Date.now();
    const expiryWithBuffer = stored.expiry - (bufferSeconds * 1000);
    return now >= expiryWithBuffer;
  },

  /**
   * Login with credentials
   */
  async login(credentials: LoginCredentials): Promise<AuthTokens> {
    const res = await fetch(`${AUTH_BASE}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(credentials)
    });

    const text = await res.text();

    if (!res.ok) {
      throw new Error(parseErrorMessage(text, `Login failed: ${res.status}`));
    }

    const data = JSON.parse(text);
    const tokens: AuthTokens = {
      accessToken: data.accessToken,
      refreshToken: data.refreshToken,
      expiresIn: data.expiresIn || 3600,
      refreshExpiresIn: data.refreshExpiresIn
    };

    this.storeTokens(tokens);
    return tokens;
  },

  /**
   * Request a password reset token
   */
  async requestPasswordReset(email: string): Promise<{ resetToken: string; note?: string }> {
    const res = await fetch(`${AUTH_BASE}/api/public/forgot-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email })
    });

    const text = await res.text();
    if (!res.ok) {
      throw new Error(parseErrorMessage(text, `Reset request failed: ${res.status}`));
    }

    return JSON.parse(text) as { resetToken: string; note?: string };
  },

  /**
   * Reset password using token + OTP
   */
  async resetPassword(payload: { email: string; resetToken: string; otp: string; newPassword: string }): Promise<void> {
    const res = await fetch(`${AUTH_BASE}/api/public/reset-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });

    const text = await res.text();
    if (!res.ok) {
      throw new Error(parseErrorMessage(text, `Reset failed: ${res.status}`));
    }
  },

  /**
   * Update password for the current user
   */
  async updatePassword(newPassword: string): Promise<void> {
    const stored = this.getStoredTokens();
    if (!stored?.accessToken) {
      throw new Error('Not authenticated');
    }

    const res = await fetch(`${AUTH_BASE}/api/me/password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${stored.accessToken}`
      },
      body: JSON.stringify({ password: newPassword })
    });

    const text = await res.text();
    if (!res.ok) {
      throw new Error(parseErrorMessage(text, `Password update failed: ${res.status}`));
    }
  },

  /**
   * Refresh the access token using the refresh token.
   *
   * Token store is wiped only when the server tells us the refresh token
   * itself is invalid (401/403/410) — *not* on transient infrastructure
   * errors. Previously a single 502/503/504 from a backend blip would
   * forcibly log the user out with no recovery.
   */
  async refreshToken(refreshToken: string): Promise<AuthTokens> {
    let res: Response;
    try {
      res = await fetch(`${AUTH_BASE}/api/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken })
      });
    } catch (networkErr) {
      // Network-level failure (DNS, offline, CORS preflight failure, etc.).
      // Don't touch storage; let the caller decide whether to retry.
      const msg = networkErr instanceof Error ? networkErr.message : 'network error';
      throw new Error(`network: ${msg}`);
    }

    if (!res.ok) {
      // Terminal: server has rejected the refresh token outright.
      //  · 400 = malformed/expired/already-rotated refresh token (some servers
      //          surface this instead of 401 — retrying is futile and just
      //          generates console noise)
      //  · 401/403 = token rejected
      //  · 410 = explicitly revoked
      // Transient (5xx, network blips, etc.): keep tokens around so the next
      // user-initiated request can re-attempt without forcing a re-login.
      if (
        res.status === 400 ||
        res.status === 401 ||
        res.status === 403 ||
        res.status === 410
      ) {
        this.clearTokens();
        throw new Error('Session expired. Please login again.');
      }
      throw new Error(`refresh transient: ${res.status}`);
    }

    const text = await res.text();
    const data = JSON.parse(text);
    const tokens: AuthTokens = {
      accessToken: data.accessToken,
      refreshToken: data.refreshToken || refreshToken,
      expiresIn: data.expiresIn || 3600,
      refreshExpiresIn: data.refreshExpiresIn
    };

    this.storeTokens(tokens);
    return tokens;
  },

  /**
   * Logout - revoke session on server and clear local tokens
   * Retries on failure to ensure session is properly revoked
   */
  async logout(): Promise<void> {
    const stored = this.getStoredTokens();

    // Try to revoke refresh token on server with retry
    if (stored?.refreshToken) {
      const maxRetries = 3;
      let lastError: Error | null = null;

      for (let attempt = 1; attempt <= maxRetries; attempt++) {
        try {
          const response = await fetch(`${AUTH_BASE}/api/auth/logout`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ refreshToken: stored.refreshToken })
          });

          if (response.ok) {
            console.log('[AuthService] Session revoked successfully');
            break;
          }

          // If server returns an error, don't retry (token might already be invalid)
          if (response.status === 401 || response.status === 400) {
            console.log('[AuthService] Session already invalid or revoked');
            break;
          }

          lastError = new Error(`Logout failed with status ${response.status}`);
        } catch (error) {
          lastError = error instanceof Error ? error : new Error('Network error during logout');
          console.warn(`[AuthService] Logout attempt ${attempt}/${maxRetries} failed:`, error);

          // Wait before retry (exponential backoff)
          if (attempt < maxRetries) {
            await new Promise(resolve => setTimeout(resolve, Math.pow(2, attempt) * 500));
          }
        }
      }

      if (lastError) {
        console.error('[AuthService] Failed to revoke session after retries:', lastError);
        // Continue to clear local tokens even if server revocation failed
      }
    }

    this.clearTokens();
  },

  /**
   * Redirect to admin portal login page
   */
  redirectToAdminLogin(): void {
    const returnUrl = encodeURIComponent(window.location.href);
    window.location.href = `${ADMIN_PORTAL_URL}?return_url=${returnUrl}`;
  },

  /**
   * Get admin portal URL for "Back to Admin" link
   */
  getAdminPortalUrl(): string {
    return ADMIN_PORTAL_URL;
  },

  /**
   * Initialize authentication - check URL params, then stored tokens
   */
  async initializeAuth(): Promise<{ accessToken: string; refreshToken: string } | null> {
    // 1. Check for token in URL (redirected from admin portal)
    const urlTokens = this.getTokenFromUrl();
    if (urlTokens) {
      // Store the tokens from URL
      const tokens: AuthTokens = {
        accessToken: urlTokens.token,
        refreshToken: urlTokens.refreshToken || '',
        expiresIn: urlTokens.expiresIn || 3600
      };

      if (urlTokens.refreshToken) {
        this.storeTokens(tokens);
      }

      return {
        accessToken: urlTokens.token,
        refreshToken: urlTokens.refreshToken || ''
      };
    }

    // 2. Check stored tokens
    const stored = this.getStoredTokens();
    if (stored) {
      // If token is expired or about to expire, try to refresh
      if (this.isTokenExpired(60)) {
        try {
          const refreshed = await this.refreshToken(stored.refreshToken);
          return {
            accessToken: refreshed.accessToken,
            refreshToken: refreshed.refreshToken
          };
        } catch {
          // Refresh failed, tokens are invalid
          this.clearTokens();
          return null;
        }
      }

      return {
        accessToken: stored.accessToken,
        refreshToken: stored.refreshToken
      };
    }

    return null;
  }
};
